import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router';
import OpenSeadragon from 'openseadragon';
import {
  Hand, Square, Pentagon, Trash2,
  ArrowLeft, Loader2, CheckCircle2,
  ChevronLeft, ChevronRight, Eye, EyeOff, RotateCcw,
} from 'lucide-react';
import { toast } from 'sonner';
import { Stage, Layer, Rect, Line, Circle, Group, Text, Label, Tag } from 'react-konva';
import { useAuth } from '@/lib/auth-context';
import {
  getMediaUrl, getAuthHeaders, isApiAssetUrl,
  assignmentsApi, getErrorMessage,
} from '@/lib/api';
import { apiToViewer, viewerToApi, type ViewerAnnotationState } from '@/lib/annotationAdapter';
import {
  buildAnnotationChanges,
  formatAnnCaption,
  revertChangeInList,
  type AnnotationChange,
} from '@/lib/annotationDiff';
import {
  getPatchAnnotations,
  markPatchVisited,
  notifyReviewSaved,
  setPatchAnnotations,
} from '@/lib/assignmentReviewSession';
import { pointInPolygon } from '@/lib/geometry';
import {
  FILTER_BY_LABEL_VALUES,
  matchesAnnotationLabelFilter,
} from '@/lib/annotationLabelFilter';
import { buildClassCountRows, classCountsFromAnnotations } from '@/lib/annotationCounts';
import { COLORS, LABEL_OPTIONS, findLabelOption, doctorLabelOptions } from '@/lib/labelTaxonomy';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import './WSIViewer.css';

type ToolMode = 'pan' | 'rectangle' | 'polygon';
interface DrawPoint { x: number; y: number }

type LocalAnnotation = ViewerAnnotationState & { _id: string };
type CompareView = 'doctor' | 'original' | 'both';

/** Idle locator ring — not a class color, so small cells stay visible on Pap stain. */
const ANNOTATION_LOCATOR_STROKE = '#7E191B';
const ANNOTATION_LOCATOR_HALO = '#FFFFFF';

function colorWithAlpha(hex: string, alpha: number): string {
  const raw = hex.replace('#', '');
  const full = raw.length === 3 ? raw.split('').map((c) => c + c).join('') : raw;
  if (full.length !== 6) return hex;
  const n = parseInt(full, 16);
  if (Number.isNaN(n)) return hex;
  const r = (n >> 16) & 255;
  const g = (n >> 8) & 255;
  const b = n & 255;
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

function mapApiAnnotations(
  anns: Parameters<typeof apiToViewer>[0][],
  prefix: string,
): LocalAnnotation[] {
  return anns.map((a, i) => {
    const v = apiToViewer(a, i);
    return { ...v, _id: `${prefix}${v.id}` };
  });
}

interface PatchAnnotatorProps {
  readOnlyMode?: boolean;
}

export function PatchAnnotator({ readOnlyMode = false }: PatchAnnotatorProps = {}) {
  const { assignmentId, patchId, sampleId } = useParams<{
    assignmentId: string;
    patchId: string;
    sampleId?: string;
  }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdminRoute = Boolean(sampleId);
  const readOnly = readOnlyMode || isAdminRoute;

  const [patch, setPatch] = useState<{ id: string; filename: string; image_path: string; width: number; height: number } | null>(null);
  const [jobTitle, setJobTitle] = useState('');
  const [readOnlyState, setReadOnly] = useState(readOnly);
  useEffect(() => { setReadOnly(readOnly); }, [readOnly]);

  const [annotations, setAnnotations] = useState<LocalAnnotation[]>([]);
  const [originalAnnotations, setOriginalAnnotations] = useState<LocalAnnotation[]>([]);
  const [doctorAnnotations, setDoctorAnnotations] = useState<LocalAnnotation[]>([]);
  const [isModified, setIsModified] = useState(false);
  const [reviewStatus, setReviewStatus] = useState<string>('not_reviewed');
  const [compareView, setCompareView] = useState<CompareView>('doctor');
  const [patchList, setPatchList] = useState<{ id: string }[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [tool, setTool] = useState<ToolMode>('pan');
  const [labelFilter, setLabelFilter] = useState<string | 'ALL'>('ALL');
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [hoveredId, setHoveredId] = useState<string | null>(null);
  const [stageSize, setStageSize] = useState({ width: 0, height: 0 });

  const [isDrawing, setIsDrawing] = useState(false);
  const [drawStart, setDrawStart] = useState<DrawPoint | null>(null);
  const [drawEnd, setDrawEnd] = useState<DrawPoint | null>(null);
  const [polygonPoints, setPolygonPoints] = useState<DrawPoint[]>([]);

  const [showLabelDialog, setShowLabelDialog] = useState(false);
  const [pendingAnnotation, setPendingAnnotation] = useState<Partial<LocalAnnotation> | null>(null);
  const [labelValue, setLabelValue] = useState<string>('NILM');
  const [sublabelValue, setSublabelValue] = useState('');
  const [selectedColor, setSelectedColor] = useState<string>(COLORS[0]);
  const [editingAnnotationId, setEditingAnnotationId] = useState<string | null>(null);

  const [isSaving, setIsSaving] = useState(false);
  const [saveStatus, setSaveStatus] = useState<'saved' | 'saving' | 'error'>('saved');
  /** True only after annotations for the current patchId finished loading. */
  const [annotationsReady, setAnnotationsReady] = useState(false);
  const [imageReady, setImageReady] = useState(false);
  const [ghostOriginal, setGhostOriginal] = useState<LocalAnnotation | null>(null);

  const viewerRef = useRef<OpenSeadragon.Viewer | null>(null);
  const osdContainerRef = useRef<HTMLDivElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);

  const [viewportBounds, setViewportBounds] = useState({ x: 0, y: 0, width: 1, height: 1 });
  const [imageSize, setImageSize] = useState({ width: 1, height: 1 });
  const [isSpacePressed, setIsSpacePressed] = useState(false);
  const [showAnnotations, setShowAnnotations] = useState(true);

  const annotationsRef = useRef(annotations);
  const originalAnnotationsRef = useRef(originalAnnotations);
  const doctorAnnotationsRef = useRef(doctorAnnotations);
  const compareViewRef = useRef(compareView);
  const toolRef = useRef(tool);
  const labelFilterRef = useRef(labelFilter);
  const showAnnotationsRef = useRef(showAnnotations);
  const hoveredIdRef = useRef(hoveredId);
  const readOnlyStateRef = useRef(readOnlyState);
  const annotationItemRefs = useRef<Record<string, HTMLDivElement | null>>({});
  const autosaveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  /** After load, ignore the annotations-effect once so we don't flash Saving. */
  const skipDirtyAutosaveRef = useRef(true);
  const saveInFlightRef = useRef(false);
  const pendingAutosaveRef = useRef(false);

  useEffect(() => { annotationsRef.current = annotations; }, [annotations]);
  useEffect(() => { originalAnnotationsRef.current = originalAnnotations; }, [originalAnnotations]);
  useEffect(() => { doctorAnnotationsRef.current = doctorAnnotations; }, [doctorAnnotations]);
  useEffect(() => { compareViewRef.current = compareView; }, [compareView]);
  useEffect(() => { toolRef.current = tool; }, [tool]);
  useEffect(() => { labelFilterRef.current = labelFilter; }, [labelFilter]);
  useEffect(() => { showAnnotationsRef.current = showAnnotations; }, [showAnnotations]);
  useEffect(() => { hoveredIdRef.current = hoveredId; }, [hoveredId]);
  useEffect(() => { readOnlyStateRef.current = readOnlyState; }, [readOnlyState]);

  const findAnnotationAtImagePoint = useCallback((x: number, y: number): LocalAnnotation | null => {
    if (!showAnnotationsRef.current) return null;
    const filter = labelFilterRef.current;
    let pools: LocalAnnotation[];
    if (isAdminRoute && compareViewRef.current === 'both') {
      pools = [...doctorAnnotationsRef.current, ...originalAnnotationsRef.current];
    } else if (isAdminRoute && compareViewRef.current === 'original') {
      pools = originalAnnotationsRef.current;
    } else if (isAdminRoute && compareViewRef.current === 'doctor') {
      pools = doctorAnnotationsRef.current.length
        ? doctorAnnotationsRef.current
        : annotationsRef.current;
    } else {
      pools = annotationsRef.current;
    }
    const currentAnnotations = pools.filter((a) =>
      matchesAnnotationLabelFilter(a, filter),
    );
    for (let i = currentAnnotations.length - 1; i >= 0; i--) {
      const ann = currentAnnotations[i];
      if (!ann.points || ann.points.length < 2) continue;
      if (ann.type === 'rectangle') {
        const p1 = ann.points[0];
        const p2 = ann.points[1];
        if (
          x >= Math.min(p1.x, p2.x) && x <= Math.max(p1.x, p2.x) &&
          y >= Math.min(p1.y, p2.y) && y <= Math.max(p1.y, p2.y)
        ) {
          return ann;
        }
      } else if (ann.points.length >= 3) {
        const ring = ann.points.map((p) => [p.x, p.y] as [number, number]);
        if (pointInPolygon(x, y, ring)) return ann;
      }
    }
    return null;
  }, [isAdminRoute]);

  // When a cell is selected (e.g. by clicking it in the viewer), reveal its
  // entry in the sidebar so the doctor can edit or delete it.
  useEffect(() => {
    if (selectedId) {
      annotationItemRefs.current[selectedId]?.scrollIntoView({
        block: 'nearest',
        behavior: 'smooth',
      });
    }
  }, [selectedId]);

  const gridBackPath = isAdminRoute
    ? `/admin/samples/${sampleId}/reviews/${assignmentId}`
    : `/doctor/assignments/${assignmentId}`;

  const patchPath = useCallback(
    (targetId: string) =>
      isAdminRoute
        ? `/admin/samples/${sampleId}/reviews/${assignmentId}/patches/${targetId}`
        : `/doctor/assignments/${assignmentId}/patches/${targetId}`,
    [isAdminRoute, sampleId, assignmentId],
  );

  useEffect(() => {
    if (!assignmentId) return;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await assignmentsApi.patches(assignmentId);
        const list = Array.isArray(data)
          ? data
          : ((data as { results?: { id: string }[] })?.results ?? []);
        if (!cancelled) setPatchList(list.map((p) => ({ id: p.id })));
      } catch {
        /* navigation strip is non-critical; ignore fetch errors */
      }
    })();
    return () => { cancelled = true; };
  }, [assignmentId]);

  const currentIndex = patchList.findIndex((p) => p.id === patchId);
  const prevPatch = currentIndex > 0 ? patchList[currentIndex - 1] : null;
  const nextPatch =
    currentIndex >= 0 && currentIndex < patchList.length - 1
      ? patchList[currentIndex + 1]
      : null;

  // Reset synchronously when the route patch changes so Prev/Next/Save stay
  // locked. Note: setState here does not apply until the next render — use
  // `isSwitchingPatch` below to lock the current render too.
  const [routePatchId, setRoutePatchId] = useState(patchId);
  const isSwitchingPatch = Boolean(patchId && routePatchId !== patchId);
  if (isSwitchingPatch && patchId) {
    setRoutePatchId(patchId);
    setLoading(true);
    setAnnotationsReady(false);
    setImageReady(false);
    setSelectedId(null);
    setHoveredId(null);
    setGhostOriginal(null);
    setAnnotations([]);
    setOriginalAnnotations([]);
    setPatch(null);
    setSaveStatus('saved');
    skipDirtyAutosaveRef.current = true;
  }

  useEffect(() => {
    if (!assignmentId || !patchId) return;
    setShowAnnotations(true);
    setAnnotationsReady(false);
    setImageReady(false);
    setSelectedId(null);
    setHoveredId(null);
    setAnnotations([]);
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        // Always fetch from API first so a corrupt session cache cannot paint
        // another patch's annotations. Trusted cache is applied only after.
        const assignmentPromise = assignmentsApi.detail(assignmentId);
        const [{ data: assignment }, { data: annData }] = await Promise.all([
          assignmentPromise,
          assignmentsApi.getAnnotations(assignmentId, patchId),
        ]);
        if (cancelled) return;

        setJobTitle(assignment.sample_name);
        setPatch({
          id: annData.patch.id,
          filename: annData.patch.filename,
          image_path: annData.patch.image_path,
          width: annData.patch.width,
          height: annData.patch.height,
        });

        const loaded = annData.annotations.map((a, i) => {
          const v = apiToViewer(a, i);
          return { ...v, _id: v.id };
        });
        const originals = mapApiAnnotations(annData.original_annotations ?? [], 'orig-');
        const doctors = mapApiAnnotations(annData.doctor_annotations ?? [], 'doc-');
        setOriginalAnnotations(
          originals.length > 0
            ? originals
            : (annData.source === 'original'
              ? loaded.map((a, i) => ({ ...a, _id: `orig-fallback-${a._id || i}` }))
              : []),
        );

        const cached = !readOnlyState ? getPatchAnnotations(assignmentId, patchId) : null;
        const working = cached ?? loaded;
        setAnnotations(working);

        if (isAdminRoute) {
          setDoctorAnnotations(doctors);
          setIsModified(Boolean(annData.is_modified));
          setReviewStatus(annData.review_status || 'not_reviewed');
          setCompareView(annData.is_modified ? 'both' : 'doctor');
        } else {
          setDoctorAnnotations([]);
          setIsModified(false);
          setReviewStatus('not_reviewed');
          setCompareView('doctor');
        }

        if (!readOnlyState) {
          // Seed trusted cache from API when none exists yet.
          if (!cached) setPatchAnnotations(assignmentId, patchId, loaded);
          markPatchVisited(assignmentId, patchId);
        }
        setAnnotationsReady(true);
      } catch (err) {
        if (!cancelled) {
          toast.error(getErrorMessage(err));
          navigate(gridBackPath);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [assignmentId, patchId, navigate, gridBackPath, readOnlyState, isAdminRoute]);

  useEffect(() => {
    if (!assignmentId || !patchId || readOnlyState || !annotationsReady || loading) return;
    if (!patch || patch.id !== patchId) return;
    setPatchAnnotations(assignmentId, patchId, annotations);
  }, [assignmentId, patchId, annotations, readOnlyState, annotationsReady, loading, patch]);

  // Derived lock: applies on the same render as a route change (unlike setState).
  const patchDataReady = Boolean(
    patch
    && patchId
    && patch.id === patchId
    && annotationsReady
    && !loading
    && !isSwitchingPatch,
  );
  // Don't block Prev/Next on background autosave; we flush before navigating.
  const navigationLocked = !patchDataReady || !imageReady;

  const clearAutosaveTimer = useCallback(() => {
    if (autosaveTimerRef.current) {
      clearTimeout(autosaveTimerRef.current);
      autosaveTimerRef.current = null;
    }
  }, []);

  /** Persist the current patch only (requires fully loaded patch). */
  const saveCurrentPatch = useCallback(async (opts?: { showStatus?: boolean }) => {
    const showStatus = opts?.showStatus !== false;
    if (!assignmentId || !patchId || readOnlyStateRef.current) return false;
    if (!patch || patch.id !== patchId || !annotationsReady) return false;

    if (saveInFlightRef.current) {
      pendingAutosaveRef.current = true;
      if (showStatus) setSaveStatus('saving');
      return false;
    }

    setPatchAnnotations(assignmentId, patchId, annotationsRef.current);
    const patchAnnotations = getPatchAnnotations(assignmentId, patchId);
    if (!patchAnnotations) return false;

    saveInFlightRef.current = true;
    setIsSaving(true);
    if (showStatus) setSaveStatus('saving');
    try {
      const payload = patchAnnotations.map((a) => viewerToApi(a));
      await assignmentsApi.saveAnnotations(assignmentId, patchId, payload);
      notifyReviewSaved(assignmentId);
      setSaveStatus('saved');
      return true;
    } catch (err) {
      setSaveStatus('error');
      toast.error(getErrorMessage(err));
      return false;
    } finally {
      saveInFlightRef.current = false;
      setIsSaving(false);
      if (pendingAutosaveRef.current) {
        pendingAutosaveRef.current = false;
        void saveCurrentPatch({ showStatus: true });
      }
    }
  }, [assignmentId, patchId, patch, annotationsReady]);

  const flushAutosave = useCallback(async () => {
    clearAutosaveTimer();
    if (readOnlyStateRef.current) return;
    if (!patchDataReady || !imageReady) return;
    await saveCurrentPatch({ showStatus: true });
  }, [clearAutosaveTimer, patchDataReady, imageReady, saveCurrentPatch]);

  // Silent baseline save once the patch is fully ready (marks PatchReview saved).
  useEffect(() => {
    if (readOnlyState || !patchDataReady || !imageReady) return;
    skipDirtyAutosaveRef.current = true;
    setSaveStatus('saved');
    let cancelled = false;
    (async () => {
      await saveCurrentPatch({ showStatus: false });
      if (!cancelled) {
        setSaveStatus('saved');
        // Allow subsequent annotation edits to trigger visible autosave.
        skipDirtyAutosaveRef.current = false;
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [readOnlyState, patchDataReady, imageReady, patchId, saveCurrentPatch]);

  // Debounced autosave when the doctor changes annotations.
  useEffect(() => {
    if (readOnlyState || !patchDataReady || !imageReady) return;
    if (skipDirtyAutosaveRef.current) return;

    setSaveStatus('saving');
    clearAutosaveTimer();
    autosaveTimerRef.current = setTimeout(() => {
      void saveCurrentPatch({ showStatus: true });
    }, 1200);

    return () => clearAutosaveTimer();
  }, [
    annotations,
    readOnlyState,
    patchDataReady,
    imageReady,
    clearAutosaveTimer,
    saveCurrentPatch,
  ]);

  // Flush on leave viewer.
  useEffect(() => {
    return () => {
      clearAutosaveTimer();
    };
  }, [clearAutosaveTimer]);

  const navigateToPatch = useCallback(async (targetId: string) => {
    if (navigationLocked) return;
    await flushAutosave();
    navigate(patchPath(targetId), { replace: true });
  }, [navigationLocked, flushAutosave, navigate, patchPath]);

  const handleBackToGrid = useCallback(() => {
    const leave = () => {
      const idx = (window.history.state as { idx?: number } | null)?.idx;
      if (typeof idx === 'number' && idx > 0) {
        navigate(-1);
      } else {
        navigate(gridBackPath, { replace: true });
      }
    };
    if (readOnlyState) {
      leave();
      return;
    }
    void flushAutosave().finally(leave);
  }, [navigate, gridBackPath, readOnlyState, flushAutosave]);

  const sublabelChoices = useMemo(() => {
    const matched = findLabelOption(labelValue);
    const priority: string[] = matched?.sublabels ? [...matched.sublabels] : [];
    const allFlat = [...new Set(LABEL_OPTIONS.flatMap((o) => [...o.sublabels]))];
    const rest = allFlat.filter((s) => !priority.includes(s)).sort((a, b) => a.localeCompare(b));
    const list = [...priority, ...rest];
    const t = sublabelValue.trim();
    if (t && !list.includes(t)) return [t, ...list];
    return list;
  }, [labelValue, sublabelValue]);

  const labelSelectEntries = useMemo(() => {
    const ordered = doctorLabelOptions();
    const v = findLabelOption(labelValue)?.value ?? labelValue?.trim();
    if (!v) return ordered;
    const known = ordered.some((o) => o.value === v);
    if (known) return ordered;
    return [
      ...ordered,
      { value: v, label: `${v} (custom)`, color: LABEL_OPTIONS[0].color, sublabels: [] as string[] },
    ];
  }, [labelValue]);

  const imageUrl = patch ? getMediaUrl(patch.image_path) : '';

  useEffect(() => {
    if (!imageUrl || !osdContainerRef.current || !patch) return;

    const authHeaders = patch && isApiAssetUrl(patch.image_path) ? getAuthHeaders() : undefined;

    const viewer = OpenSeadragon({
      element: osdContainerRef.current,
      tileSources: {
        type: 'image',
        url: imageUrl,
        buildPyramid: false,
        crossOriginPolicy: 'Anonymous',
      } as OpenSeadragon.TileSourceOptions,
      ajaxHeaders: authHeaders,
      prefixUrl: 'https://openseadragon.github.io/openseadragon/images/',
      showNavigationControl: true,
      showNavigator: true,
      navigatorPosition: 'BOTTOM_LEFT',
      navigatorSizeRatio: 0.15,
      minZoomLevel: 0.5,
      maxZoomLevel: 40,
      visibilityRatio: 0.5,
      constrainDuringPan: false,
      animationTime: 0.3,
      crossOriginPolicy: 'Anonymous',
      gestureSettingsMouse: { clickToZoom: false, dblClickToZoom: true },
    });

    const updateBounds = () => {
      const bounds = viewer.viewport.getBounds();
      setViewportBounds({
        x: bounds.x,
        y: bounds.y,
        width: bounds.width,
        height: bounds.height,
      });
    };

    viewer.addHandler('open', () => {
      viewer.viewport.goHome(true);
      const tiledImage = viewer.world.getItemAt(0);
      if (tiledImage) {
        const size = tiledImage.getContentSize();
        if (size.x > 0 && size.y > 0) {
          setImageSize({ width: size.x, height: size.y });
        }
      }
      setImageReady(true);
    });

    viewer.addHandler('open-failed', () => {
      setImageReady(true); // unblock UI even if tile fails; annotations still usable
      toast.error('Failed to load patch image');
    });

    viewer.addHandler('animation', updateBounds);
    viewer.addHandler('animation-finish', updateBounds);
    viewer.addHandler('resize', updateBounds);
    viewer.addHandler('canvas-drag', updateBounds);
    viewer.addHandler('canvas-scroll', updateBounds);
    viewer.addHandler('update-viewport', updateBounds);

    const mouseTracker = new OpenSeadragon.MouseTracker({
      element: viewer.container,
      moveHandler: (evt) => {
        const imgPt = viewer.viewport.viewerElementToImageCoordinates(evt.position);
        setHoveredId(findAnnotationAtImagePoint(imgPt.x, imgPt.y)?._id ?? null);
      },
      exitHandler: () => setHoveredId(null),
    });

    // Use OSD's canvas-click (with the `quick` flag) so a genuine click selects
    // a cell, while drags used for panning are ignored.
    const handleCanvasClick = (event: OpenSeadragon.CanvasClickEvent) => {
      if (toolRef.current !== 'pan') return;
      if (!event.quick) return;
      const imgPt = viewer.viewport.viewerElementToImageCoordinates(event.position);
      setSelectedId(findAnnotationAtImagePoint(imgPt.x, imgPt.y)?._id ?? null);
    };
    viewer.addHandler('canvas-click', handleCanvasClick);

    viewerRef.current = viewer;

    return () => {
      viewer.removeHandler('canvas-click', handleCanvasClick);
      mouseTracker.destroy();
      viewer.destroy();
      viewerRef.current = null;
      setImageReady(false);
    };
  }, [imageUrl, patch, findAnnotationAtImagePoint]);

  useEffect(() => {
    const viewer = viewerRef.current;
    if (!viewer) return;
    const isDrawingMode = tool !== 'pan' && !isSpacePressed;
    viewer.setMouseNavEnabled(!isDrawingMode);
  }, [tool, isSpacePressed]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.code === 'Space' && !e.repeat && document.activeElement?.tagName !== 'INPUT') {
        setIsSpacePressed(true);
      }
    };
    const handleKeyUp = (e: KeyboardEvent) => {
      if (e.code === 'Space') setIsSpacePressed(false);
    };
    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('keyup', handleKeyUp);
    const handleBlur = () => setIsSpacePressed(false);
    window.addEventListener('blur', handleBlur);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('keyup', handleKeyUp);
      window.removeEventListener('blur', handleBlur);
    };
  }, []);

  useEffect(() => {
    const updateSize = () => {
      if (panelRef.current) {
        setStageSize({
          width: panelRef.current.clientWidth,
          height: panelRef.current.clientHeight,
        });
      }
    };
    updateSize();
    const ro = new ResizeObserver(updateSize);
    if (panelRef.current) ro.observe(panelRef.current);
    window.addEventListener('resize', updateSize);
    return () => {
      window.removeEventListener('resize', updateSize);
      ro.disconnect();
    };
  }, [loading]);

  const imageToScreen = useCallback((pt: DrawPoint): DrawPoint => {
    const viewer = viewerRef.current;
    if (!viewer || imageSize.width === 0) return { x: 0, y: 0 };
    const screenPt = viewer.viewport.imageToViewerElementCoordinates(
      new OpenSeadragon.Point(pt.x, pt.y),
    );
    return { x: screenPt.x, y: screenPt.y };
  }, [viewportBounds, imageSize, stageSize]);

  const screenToImage = useCallback((pt: DrawPoint): DrawPoint => {
    const viewer = viewerRef.current;
    if (!viewer || imageSize.width === 0) return { x: 0, y: 0 };
    const imgPt = viewer.viewport.viewerElementToImageCoordinates(
      new OpenSeadragon.Point(pt.x, pt.y),
    );
    return { x: imgPt.x, y: imgPt.y };
  }, [viewportBounds, imageSize, stageSize]);

  const patchMeta = useCallback(
    (): Partial<LocalAnnotation> => ({
      id: `local-${Date.now()}`,
      _id: `local-${Date.now()}`,
      type: 'rectangle',
      points: [],
      label: 'Normal',
      sublabel: '',
      color: COLORS[0],
    }),
    [],
  );

  const openLabelDialog = (ann: Partial<LocalAnnotation>, editingId: string | null = null) => {
    setPendingAnnotation(ann);
    setEditingAnnotationId(editingId);
    setLabelValue('NILM');
    setSublabelValue('None');
    setSelectedColor(LABEL_OPTIONS[0].color);
    setShowLabelDialog(true);
  };

  const handleStageMouseDown = (e: { evt: MouseEvent; target: { getStage: () => { getPointerPosition: () => DrawPoint | null } | null } }) => {
    if (readOnlyState || tool === 'pan' || isSpacePressed) return;
    if (e.evt.button === 2) return;
    const point = e.target.getStage()?.getPointerPosition();
    if (!point) return;
    if (tool === 'rectangle') {
      setIsDrawing(true);
      setDrawStart(point);
      setDrawEnd(point);
    } else if (tool === 'polygon') {
      setPolygonPoints((prev) => [...prev, point]);
    }
  };

  const handleStageMouseMove = (e: { target: { getStage: () => { getPointerPosition: () => DrawPoint | null } | null } }) => {
    const point = e.target.getStage()?.getPointerPosition();
    if (point) setDrawEnd(point);
  };

  const finishPolygon = () => {
    if (polygonPoints.length >= 3) {
      const imagePoints = polygonPoints.map((p) => screenToImage(p));
      openLabelDialog({
        ...patchMeta(),
        type: 'polygon',
        points: imagePoints,
        color: LABEL_OPTIONS[0].color,
      });
      setPolygonPoints([]);
      setDrawEnd(null);
    } else {
      setPolygonPoints([]);
      setDrawEnd(null);
    }
  };

  const handleStageMouseUp = () => {
    if (tool === 'rectangle' && isDrawing && drawStart && drawEnd) {
      setIsDrawing(false);
      const p1 = screenToImage(drawStart);
      const p2 = screenToImage(drawEnd);
      const dx = drawEnd.x - drawStart.x;
      const dy = drawEnd.y - drawStart.y;
      if (Math.sqrt(dx * dx + dy * dy) > 5) {
        openLabelDialog({
          ...patchMeta(),
          type: 'rectangle',
          points: [
            { x: Math.min(p1.x, p2.x), y: Math.min(p1.y, p2.y) },
            { x: Math.max(p1.x, p2.x), y: Math.max(p1.y, p2.y) },
          ],
          color: LABEL_OPTIONS[0].color,
        });
      }
      setDrawStart(null);
      setDrawEnd(null);
    }
  };

  const handleWheel = (e: { evt: WheelEvent }) => {
    e.evt.preventDefault();
    const viewer = viewerRef.current;
    if (!viewer) return;
    const zoomFactor = 1.1;
    const point = viewer.viewport.pointFromPixel(
      new OpenSeadragon.Point(e.evt.clientX, e.evt.clientY),
    );
    if (e.evt.deltaY < 0) {
      viewer.viewport.zoomBy(zoomFactor, point);
    } else {
      viewer.viewport.zoomBy(1 / zoomFactor, point);
    }
    viewer.viewport.applyConstraints();
  };

  const handleSaveLabel = () => {
    const opt = findLabelOption(labelValue);
    const canonicalLabel = opt?.value ?? labelValue.trim();
    const newColor = selectedColor || opt?.color || COLORS[0];
    const resolvedSublabel = sublabelValue.trim() || 'None';

    if (editingAnnotationId) {
      setAnnotations((prev) =>
        prev.map((a) =>
          a._id === editingAnnotationId
            ? { ...a, label: canonicalLabel, sublabel: resolvedSublabel, color: newColor }
            : a,
        ),
      );
    } else if (pendingAnnotation) {
      const id = `local-${Date.now()}`;
      const newAnn: LocalAnnotation = {
        id,
        _id: id,
        type: pendingAnnotation.type || 'rectangle',
        points: pendingAnnotation.points || [],
        label: canonicalLabel,
        sublabel: resolvedSublabel,
        color: newColor,
      };
      setAnnotations((prev) => [newAnn, ...prev]);
    }
    setShowLabelDialog(false);
    setPendingAnnotation(null);
    setEditingAnnotationId(null);
  };

  const handleDeleteAnnotation = (id: string) => {
    if (readOnlyState) return;
    setAnnotations((prev) => prev.filter((a) => a._id !== id));
    if (selectedId === id) setSelectedId(null);
  };

  const handleEditLabel = (ann: LocalAnnotation) => {
    if (readOnlyState) return;
    const matched = findLabelOption(ann.label || 'NILM');
    setEditingAnnotationId(ann._id || null);
    setLabelValue(matched?.value ?? (ann.label || 'NILM'));
    setSublabelValue(ann.sublabel?.trim() || 'None');
    setSelectedColor(ann.color || matched?.color || LABEL_OPTIONS[0].color);
    setPendingAnnotation(null);
    setSelectedId(ann._id || null);
    setShowLabelDialog(true);
  };

  const handleEditLabelRef = useRef(handleEditLabel);
  useEffect(() => { handleEditLabelRef.current = handleEditLabel; }, [handleEditLabel]);

  /** Right-click a cell → open Edit Annotation Label (same dialog as sidebar edit). */
  const openEditFromContextMenu = useCallback((clientX: number, clientY: number) => {
    if (readOnlyStateRef.current) return false;
    if (!showAnnotationsRef.current) return false;

    const hovered = hoveredIdRef.current;
    if (hovered) {
      const ann = annotationsRef.current.find((a) => a._id === hovered);
      if (ann) {
        handleEditLabelRef.current(ann);
        return true;
      }
    }

    const panel = panelRef.current;
    const viewer = viewerRef.current;
    if (panel && viewer) {
      const rect = panel.getBoundingClientRect();
      const local = { x: clientX - rect.left, y: clientY - rect.top };
      const imgPt = viewer.viewport.viewerElementToImageCoordinates(
        new OpenSeadragon.Point(local.x, local.y),
      );
      const ann = findAnnotationAtImagePoint(imgPt.x, imgPt.y);
      if (ann) {
        handleEditLabelRef.current(ann);
        return true;
      }
    }
    return false;
  }, [findAnnotationAtImagePoint]);

  // Catch right-clicks on the OSD surface (pan mode) as well as Konva overlay.
  useEffect(() => {
    const panel = panelRef.current;
    if (!panel) return;
    const onContextMenu = (e: MouseEvent) => {
      e.preventDefault();
      if (openEditFromContextMenu(e.clientX, e.clientY)) return;
      if (toolRef.current === 'polygon' && !readOnlyStateRef.current) {
        // Polygon finish is handled from Konva when drawing; ignore here.
      }
    };
    panel.addEventListener('contextmenu', onContextMenu);
    return () => panel.removeEventListener('contextmenu', onContextMenu);
  }, [openEditFromContextMenu, patch]);

  const activeAnnotations = useMemo(() => {
    if (!isAdminRoute) return annotations;
    if (compareView === 'original') return originalAnnotations;
    if (compareView === 'both') {
      return doctorAnnotations.length > 0 ? doctorAnnotations : originalAnnotations;
    }
    return doctorAnnotations.length > 0 ? doctorAnnotations : annotations;
  }, [isAdminRoute, compareView, annotations, originalAnnotations, doctorAnnotations]);

  const filteredAnnotations = activeAnnotations.filter((a) =>
    matchesAnnotationLabelFilter(a, labelFilter),
  );

  const filteredOriginals = originalAnnotations.filter((a) =>
    matchesAnnotationLabelFilter(a, labelFilter),
  );
  const filteredDoctors = doctorAnnotations.filter((a) =>
    matchesAnnotationLabelFilter(a, labelFilter),
  );

  const manualClassRows = useMemo(
    () => buildClassCountRows(classCountsFromAnnotations(activeAnnotations)),
    [activeAnnotations],
  );

  const doctorChanges = useMemo(() => {
    if (isAdminRoute || !annotationsReady) return [];
    return buildAnnotationChanges(originalAnnotations, annotations);
  }, [isAdminRoute, annotationsReady, originalAnnotations, annotations]);

  const handleChangeHover = useCallback((change: AnnotationChange | null) => {
    if (!change) {
      setHoveredId(null);
      setGhostOriginal(null);
      return;
    }
    if (change.kind === 'removed' && change.original) {
      setHoveredId(null);
      setGhostOriginal(change.original);
      return;
    }
    setGhostOriginal(null);
    if (change.current) setHoveredId(change.current._id);
  }, []);

  const handleRevertChange = useCallback((change: AnnotationChange) => {
    if (readOnlyState || navigationLocked) return;
    setAnnotations((prev) => revertChangeInList(prev, change));
    setSelectedId(null);
    setHoveredId(null);
    setGhostOriginal(null);
    toast.success(
      change.kind === 'added'
        ? 'Added cell removed'
        : change.kind === 'removed'
          ? 'Original cell restored'
          : 'Cell reverted to original',
    );
  }, [readOnlyState, navigationLocked]);

  const reviewBadgeLabel = useMemo(() => {
    if (!isAdminRoute) return null;
    if (reviewStatus !== 'saved') return 'Not reviewed';
    return isModified ? 'Reannotated' : 'Approved';
  }, [isAdminRoute, reviewStatus, isModified]);

  const hoveredAnnotation = useMemo(
    () => {
      const pools = isAdminRoute && compareView === 'both'
        ? [...doctorAnnotations, ...originalAnnotations]
        : activeAnnotations;
      return pools.find((a) => a._id === hoveredId) ?? null;
    },
    [isAdminRoute, compareView, doctorAnnotations, originalAnnotations, activeAnnotations, hoveredId],
  );

  const renderAnnotationShape = (
    ann: LocalAnnotation,
    opts?: { dashed?: boolean; opacityScale?: number },
  ) => {
    if (!ann.points || ann.points.length < 2) return null;
    const isSelected = selectedId === ann._id;
    const isHovered = hoveredId === ann._id;
    const shouldPoint = isSelected || isHovered || labelFilter !== 'ALL';
    const color = ann.color || '#FF4444';
    const dashed = opts?.dashed ?? false;
    const dash = dashed ? [8, 5] : undefined;
    const opacityScale = opts?.opacityScale ?? 1;
    const fillOpacity = (isHovered ? 1 : 0.5) * opacityScale;
    const fill = isHovered ? 'transparent' : colorWithAlpha(color, fillOpacity);
    const showLocator = !isHovered;
    const haloWidth = isSelected ? 4.5 : 3.5;
    const strokeColor = isHovered ? '#000000' : ANNOTATION_LOCATOR_STROKE;
    const strokeWidth = isHovered ? 1 : (isSelected ? 2.5 : 2);

    if (ann.type === 'rectangle') {
      const p1 = imageToScreen(ann.points[0]);
      const p2 = imageToScreen(ann.points[1]);
      const x = Math.min(p1.x, p2.x);
      const y = Math.min(p1.y, p2.y);
      const w = Math.abs(p2.x - p1.x);
      const h = Math.abs(p2.y - p1.y);
      return (
        <Group key={ann._id}>
          {showLocator && (
            <Rect
              x={x} y={y} width={w} height={h}
              fillEnabled={false}
              stroke={ANNOTATION_LOCATOR_HALO}
              strokeWidth={haloWidth}
              dash={dash}
              listening={false}
            />
          )}
          <Rect
            x={x} y={y} width={w} height={h}
            fill={fill}
            stroke={strokeColor} strokeWidth={strokeWidth}
            dash={dash}
          />
          {(ann.label && shouldPoint) && (
            <Label x={x + w / 2} y={y}>
              <Tag fill="rgba(0,0,0,0.7)" cornerRadius={4} pointerDirection="down" pointerWidth={10} pointerHeight={5} />
              <Text text={ann.label} fill="#fff" fontSize={12} fontStyle="bold" padding={5} align="center" />
            </Label>
          )}
        </Group>
      );
    }

    const screenPoints = ann.points.map((p) => imageToScreen(p));
    const flatPoints = screenPoints.flatMap((p) => [p.x, p.y]);
    const center = {
      x: screenPoints.reduce((s, p) => s + p.x, 0) / screenPoints.length,
      y: screenPoints.reduce((s, p) => s + p.y, 0) / screenPoints.length,
    };
    const topY = Math.min(...screenPoints.map((p) => p.y));

    return (
      <Group key={ann._id}>
        {showLocator && (
          <Line
            points={flatPoints} closed
            fillEnabled={false}
            stroke={ANNOTATION_LOCATOR_HALO}
            strokeWidth={haloWidth}
            dash={dash}
            listening={false}
          />
        )}
        <Line
          points={flatPoints} closed
          fill={fill}
          stroke={strokeColor} strokeWidth={strokeWidth}
          dash={dash}
        />
        {(ann.label && shouldPoint) && (
          <Label x={center.x} y={topY}>
            <Tag fill="rgba(0,0,0,0.8)" cornerRadius={4} pointerDirection="down" pointerWidth={10} pointerHeight={5} />
            <Text text={ann.label} fill="#fff" fontSize={12} fontStyle="bold" padding={5} align="center" />
          </Label>
        )}
      </Group>
    );
  };

  const renderAnnotations = () => {
    if (!showAnnotations) return null;
    if (isAdminRoute && compareView === 'both') {
      return (
        <>
          {filteredOriginals.map((ann) => renderAnnotationShape(ann, { dashed: true, opacityScale: 0.55 }))}
          {filteredDoctors.map((ann) => renderAnnotationShape(ann))}
        </>
      );
    }
    return (
      <>
        {ghostOriginal && renderAnnotationShape(
          { ...ghostOriginal, _id: `ghost-${ghostOriginal._id}` },
          { dashed: true, opacityScale: 0.85 },
        )}
        {filteredAnnotations.map((ann) => renderAnnotationShape(ann))}
      </>
    );
  };

  const renderDrawPreview = () => {
    if (tool === 'rectangle' && isDrawing && drawStart && drawEnd) {
      const x = Math.min(drawStart.x, drawEnd.x);
      const y = Math.min(drawStart.y, drawEnd.y);
      const w = Math.abs(drawEnd.x - drawStart.x);
      const h = Math.abs(drawEnd.y - drawStart.y);
      return (
        <Rect x={x} y={y} width={w} height={h} fill="rgba(59, 130, 246, 0.2)" stroke="#3b82f6" strokeWidth={2} dash={[6, 3]} />
      );
    }
    if (tool === 'polygon' && polygonPoints.length > 0) {
      const flatPoints = polygonPoints.flatMap((p) => [p.x, p.y]);
      const lastPoint = polygonPoints[polygonPoints.length - 1];
      return (
        <Group>
          <Line points={flatPoints} stroke="#3b82f6" strokeWidth={2} closed={false} />
          {drawEnd && (
            <Line points={[lastPoint.x, lastPoint.y, drawEnd.x, drawEnd.y]} stroke="#3b82f6" strokeWidth={2} dash={[4, 4]} />
          )}
          {polygonPoints.map((p, i) => (
            <Circle key={i} x={p.x} y={p.y} radius={4} fill="#3b82f6" stroke="#fff" strokeWidth={2} />
          ))}
        </Group>
      );
    }
    return null;
  };

  const renderHoverCard = () => {
    if (!showAnnotations || !hoveredAnnotation) return null;
    const ann = hoveredAnnotation;
    const labelOpt = findLabelOption(ann.label || '');
    return (
      <div className="wsi-cell-hover-card" role="tooltip">
        <div className="wsi-cell-hover-card-header">
          <span className="wsi-cell-hover-swatch" style={{ background: ann.color || labelOpt?.color || '#94a3b8' }} />
          <div>
            <p className="wsi-cell-hover-title">{labelOpt?.label ?? ann.label}</p>
            {ann.sublabel && <p className="wsi-cell-hover-subtitle">{ann.sublabel}</p>}
          </div>
        </div>
      </div>
    );
  };

  const toggleAnnotations = () => {
    setShowAnnotations((prev) => {
      const next = !prev;
      if (!next) {
        setSelectedId(null);
        setHoveredId(null);
        setTool('pan');
        setPolygonPoints([]);
        setDrawEnd(null);
      }
      return next;
    });
  };

  if (!patchDataReady) {
    return (
      <div className="wsi-viewer-root">
        <div className="wsi-toolbar" aria-busy="true">
          <div className="wsi-toolbar-title">
            <span className="truncate max-w-[280px]">{jobTitle || 'Loading…'}</span>
          </div>
          <Button variant="ghost" size="sm" className="wsi-tool-btn" disabled>
            <ChevronLeft className="size-4 mr-1" /> Prev
          </Button>
          <span className="text-xs text-slate-400 font-mono px-1 select-none">Loading…</span>
          <Button variant="ghost" size="sm" className="wsi-tool-btn" disabled>
            Next <ChevronRight className="size-4 ml-1" />
          </Button>
          <div className="wsi-separator" />
          <Button
            variant="ghost" size="sm"
            className="wsi-tool-btn"
            onClick={handleBackToGrid}
          >
            <ArrowLeft className="size-4 mr-2" /> Patch grid
          </Button>
        </div>
        <div className="wsi-loading">
          <div className="wsi-loading-spinner" />
          <p style={{ color: '#94a3b8', fontSize: 14 }}>Loading patch…</p>
        </div>
      </div>
    );
  }

  if (!patch) return null;

  return (
    <div className="wsi-viewer-root">
      <div className="wsi-toolbar">
        <div className="wsi-toolbar-title">
          <span className="truncate max-w-[280px]">{jobTitle}</span>
          <span className="text-slate-500 font-mono text-xs">
            {patch.filename}
          </span>
        </div>

        {!readOnlyState && (
          <div className="wsi-tool-group">
            <Button
              variant="ghost" size="sm"
              className={`wsi-tool-btn ${tool === 'pan' ? 'active' : ''}`}
              disabled={navigationLocked}
              onClick={() => { setTool('pan'); setPolygonPoints([]); setDrawEnd(null); }}
            >
              <Hand className="size-4 mr-2" /> Pan
            </Button>
            <Button
              variant="ghost" size="sm"
              className={`wsi-tool-btn ${tool === 'rectangle' ? 'active' : ''}`}
              disabled={!showAnnotations || navigationLocked}
              onClick={() => { setTool('rectangle'); setPolygonPoints([]); setDrawEnd(null); }}
            >
              <Square className="size-4 mr-2" /> Rect
            </Button>
            <Button
              variant="ghost" size="sm"
              className={`wsi-tool-btn ${tool === 'polygon' ? 'active' : ''}`}
              disabled={!showAnnotations || navigationLocked}
              onClick={() => { setTool('polygon'); setPolygonPoints([]); setDrawEnd(null); }}
            >
              <Pentagon className="size-4 mr-2" /> Poly
            </Button>
            <div className="wsi-separator" />
            <Button
              variant="ghost" size="sm"
              className="wsi-tool-btn danger"
              disabled={!selectedId || !showAnnotations || navigationLocked}
              onClick={() => selectedId && handleDeleteAnnotation(selectedId)}
            >
              <Trash2 className="size-4 mr-1" /> Delete
            </Button>
          </div>
        )}

        <div className="wsi-separator" />

        {!readOnlyState && (
          <>
            <Button
              variant="ghost" size="sm"
              className={`wsi-tool-btn ${showAnnotations ? 'active' : ''}`}
              disabled={navigationLocked}
              onClick={toggleAnnotations}
            >
              {showAnnotations ? (
                <EyeOff className="size-4 mr-2" />
              ) : (
                <Eye className="size-4 mr-2" />
              )}
              {showAnnotations ? 'Hide annotations' : 'Show annotations'}
            </Button>
            <button
              type="button"
              className={`wsi-autosave-chip ${saveStatus === 'error' ? 'error' : ''}`}
              disabled={navigationLocked && saveStatus !== 'error'}
              title={
                saveStatus === 'error'
                  ? 'Save failed — click to retry'
                  : saveStatus === 'saving'
                    ? 'Saving changes…'
                    : 'All changes saved'
              }
              onClick={() => {
                if (saveStatus === 'error') void flushAutosave();
              }}
            >
              {saveStatus === 'saving' ? (
                <Loader2 className="size-3.5 animate-spin" />
              ) : saveStatus === 'error' ? (
                <span className="wsi-autosave-dot" />
              ) : (
                <CheckCircle2 className="size-3.5 text-green-400" />
              )}
              <span>
                {saveStatus === 'saving' ? 'Saving…' : saveStatus === 'error' ? 'Save failed' : 'Saved'}
              </span>
            </button>
          </>
        )}

        <Button
          variant="ghost" size="sm"
          className="wsi-tool-btn"
          disabled={!prevPatch || navigationLocked || isSaving}
          title={navigationLocked ? 'Wait for the patch to finish loading' : undefined}
          onClick={() => {
            if (!prevPatch) return;
            void navigateToPatch(prevPatch.id);
          }}
        >
          <ChevronLeft className="size-4 mr-1" /> Prev
        </Button>
        {patchList.length > 0 && currentIndex >= 0 && (
          <span className="text-xs text-slate-400 font-mono px-1 select-none">
            {currentIndex + 1} / {patchList.length}
            {navigationLocked ? ' · Loading…' : ''}
          </span>
        )}
        <Button
          variant="ghost" size="sm"
          className="wsi-tool-btn"
          disabled={!nextPatch || navigationLocked || isSaving}
          title={navigationLocked ? 'Wait for the patch to finish loading' : undefined}
          onClick={() => {
            if (!nextPatch) return;
            void navigateToPatch(nextPatch.id);
          }}
        >
          Next <ChevronRight className="size-4 ml-1" />
        </Button>

        <div className="wsi-separator" />

        <Button
          variant="ghost" size="sm"
          className="wsi-tool-btn"
          onClick={handleBackToGrid}
        >
          <ArrowLeft className="size-4 mr-2" /> Patch grid
        </Button>
      </div>

      <div className="wsi-content">
        <div className="wsi-viewer-panel" ref={panelRef}>
          <div ref={osdContainerRef} className="wsi-osd-container" />
          {!imageReady && (
            <div className="wsi-loading wsi-loading-overlay">
              <div className="wsi-loading-spinner" />
              <p style={{ color: '#94a3b8', fontSize: 14 }}>Loading image…</p>
            </div>
          )}
          {renderHoverCard()}
          <div className={`wsi-konva-overlay ${tool === 'pan' || isSpacePressed ? '' : 'drawing'}`}>
            {stageSize.width > 0 && (
              <Stage
                width={stageSize.width}
                height={stageSize.height}
                onMouseDown={readOnlyState ? undefined : handleStageMouseDown}
                onMouseMove={readOnlyState ? undefined : handleStageMouseMove}
                onMouseUp={readOnlyState ? undefined : handleStageMouseUp}
                // onDblClick={readOnlyState ? undefined : () => tool === 'polygon' && finishPolygon()}
                onContextMenu={(e) => {
                  e.evt.preventDefault();
                  if (openEditFromContextMenu(e.evt.clientX, e.evt.clientY)) return;
                  if (!readOnlyState && tool === 'polygon') finishPolygon();
                }}
                onWheel={handleWheel}
                style={{ cursor: tool === 'pan' || isSpacePressed ? 'grab' : 'crosshair' }}
              >
                <Layer>
                  <Rect
                    x={0} y={0} width={stageSize.width} height={stageSize.height}
                    fill="rgba(0,0,0,0)"
                    listening={tool !== 'pan' && !isSpacePressed && !readOnlyState}
                  />
                  {renderAnnotations()}
                  {!readOnlyState && showAnnotations && renderDrawPreview()}
                </Layer>
              </Stage>
            )}
          </div>
        </div>

        <div className="wsi-sidebar">
          <div className="wsi-sidebar-header">
            <span>Annotations</span>
            <span className="wsi-sidebar-count">
              {isAdminRoute && compareView === 'both'
                ? `${filteredDoctors.length} / ${filteredOriginals.length}`
                : filteredAnnotations.length}
            </span>
          </div>

          {isAdminRoute && reviewBadgeLabel && (
            <div className="px-4 py-3 border-b border-slate-700 space-y-2">
              <div className="flex items-center justify-between gap-2">
                <span
                  className={`wsi-source-badge ${isModified ? 'manual' : 'ai'}`}
                >
                  {reviewBadgeLabel}
                </span>
                {reviewStatus === 'saved' && (
                  <span className="text-[11px] text-slate-400">
                    Orig {originalAnnotations.length} · Doctor {doctorAnnotations.length}
                  </span>
                )}
              </div>
              {(doctorAnnotations.length > 0 || originalAnnotations.length > 0) && (
                <div className="wsi-compare-tabs">
                  {([
                    ['doctor', 'Doctor'],
                    ['original', 'Original'],
                    ['both', 'Both'],
                  ] as const).map(([value, label]) => (
                    <button
                      key={value}
                      type="button"
                      className={`wsi-compare-tab ${compareView === value ? 'active' : ''}`}
                      onClick={() => {
                        setCompareView(value);
                        setSelectedId(null);
                        setHoveredId(null);
                      }}
                    >
                      {label}
                    </button>
                  ))}
                </div>
              )}
              {isModified && compareView === 'both' && (
                <p className="text-[11px] text-slate-400 leading-snug">
                  Dashed = original upload · Solid = doctor reannotation
                </p>
              )}
            </div>
          )}

          {!isAdminRoute && (
            <div className="wsi-changes-panel border-b border-slate-700">
              <div className="wsi-sidebar-subheader">
                Changes made
                <span className="wsi-sidebar-subcount">{doctorChanges.length}</span>
              </div>
              {doctorChanges.length === 0 ? (
                <p className="wsi-sidebar-empty px-4 py-3">No changes from original</p>
              ) : (
                <div className="wsi-annotation-list wsi-changes-list">
                  {doctorChanges.map((change) => (
                    <div
                      key={change.id}
                      className={`wsi-change-item ${
                        (change.current && (hoveredId === change.current._id || selectedId === change.current._id))
                        || (change.kind === 'removed' && ghostOriginal?._id === change.original?._id)
                          ? 'selected'
                          : ''
                      }`}
                      onMouseEnter={() => handleChangeHover(change)}
                      onMouseLeave={() => handleChangeHover(null)}
                      onClick={() => {
                        if (change.current) setSelectedId(change.current._id);
                        else handleChangeHover(change);
                      }}
                    >
                      <div className="wsi-change-body">
                        <span className={`wsi-source-badge ${
                          change.kind === 'added' ? 'manual' : change.kind === 'removed' ? 'ai' : 'manual'
                        }`}>
                          {change.kind === 'changed' ? 'Changed' : change.kind === 'added' ? 'Added' : 'Removed'}
                        </span>
                        {change.kind === 'changed' && (
                          <div className="wsi-change-diff">
                            <div className="wsi-change-line">
                              <span className="wsi-change-key">Original</span>
                              <span className="wsi-change-val">{formatAnnCaption(change.original)}</span>
                            </div>
                            <div className="wsi-change-line">
                              <span className="wsi-change-key">Yours</span>
                              <span className="wsi-change-val">{formatAnnCaption(change.current)}</span>
                            </div>
                          </div>
                        )}
                        {change.kind === 'added' && (
                          <div className="wsi-change-diff">
                            <div className="wsi-change-line">
                              <span className="wsi-change-key">Yours</span>
                              <span className="wsi-change-val">{formatAnnCaption(change.current)}</span>
                            </div>
                          </div>
                        )}
                        {change.kind === 'removed' && (
                          <div className="wsi-change-diff">
                            <div className="wsi-change-line">
                              <span className="wsi-change-key">Original</span>
                              <span className="wsi-change-val">{formatAnnCaption(change.original)}</span>
                            </div>
                          </div>
                        )}
                      </div>
                      <button
                        type="button"
                        className="wsi-ann-action-btn"
                        title="Revert to original"
                        disabled={navigationLocked}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleRevertChange(change);
                        }}
                      >
                        <RotateCcw className="size-3.5" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {isAdminRoute && manualClassRows.length > 0 && (
            <div className="wsi-annotation-list border-b border-slate-700">
              {manualClassRows.map((row) => (
                <button
                  key={row.value}
                  type="button"
                  className={`wsi-class-count-item wsi-class-count-item--manual ${labelFilter === row.value ? 'selected' : ''}`}
                  onClick={() => setLabelFilter((prev) => (prev === row.value ? 'ALL' : row.value))}
                >
                  <div className="wsi-annotation-color" style={{ background: row.color }} />
                  <div className="wsi-annotation-info">
                    <div className="wsi-annotation-label">{row.label}</div>
                  </div>
                  <span className="wsi-class-count-number">{row.count}</span>
                </button>
              ))}
            </div>
          )}

          {(activeAnnotations.length > 0 || originalAnnotations.length > 0 || doctorAnnotations.length > 0) && (
            <div className="px-4 py-3 border-b border-slate-700 bg-slate-900/50">
              <Select value={labelFilter} onValueChange={setLabelFilter}>
                <SelectTrigger className="w-full h-8 text-xs bg-slate-800 border-slate-700 text-slate-200">
                  <SelectValue placeholder="All Annotations" />
                </SelectTrigger>
                <SelectContent className="bg-slate-800 border-slate-700 text-slate-200">
                  <SelectItem value="ALL">All Annotations</SelectItem>
                  {FILTER_BY_LABEL_VALUES.map((value) => {
                    const opt = LABEL_OPTIONS.find((o) => o.value === value);
                    return (
                      <SelectItem key={value} value={value}>
                        {opt?.label ?? value}
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>
            </div>
          )}

          <div className="wsi-annotation-list">
            {isAdminRoute && compareView === 'both' ? (
              <>
                <div className="wsi-sidebar-subheader">
                  Original <span className="wsi-sidebar-subcount">{filteredOriginals.length}</span>
                </div>
                {filteredOriginals.length === 0 ? (
                  <p className="wsi-sidebar-empty">No original annotations</p>
                ) : (
                  filteredOriginals.map((ann) => (
                    <div
                      key={ann._id}
                      ref={(el) => { annotationItemRefs.current[ann._id] = el; }}
                      className={`wsi-annotation-item ${selectedId === ann._id || hoveredId === ann._id ? 'selected' : ''}`}
                      onClick={() => setSelectedId(ann._id || null)}
                      onMouseEnter={() => setHoveredId(ann._id || null)}
                      onMouseLeave={() => setHoveredId(null)}
                    >
                      <div className="wsi-annotation-color" style={{ background: ann.color || '#FF4444' }} />
                      <div className="wsi-annotation-info">
                        <div className="wsi-annotation-label">{ann.label || 'Unlabeled'}</div>
                        {ann.sublabel && <div className="wsi-annotation-sublabel">{ann.sublabel}</div>}
                        <div className="wsi-annotation-meta">
                          <span className="wsi-source-badge ai">Original</span>
                        </div>
                      </div>
                    </div>
                  ))
                )}
                <div className="wsi-sidebar-subheader">
                  Doctor <span className="wsi-sidebar-subcount">{filteredDoctors.length}</span>
                </div>
                {filteredDoctors.length === 0 ? (
                  <p className="wsi-sidebar-empty">No doctor annotations</p>
                ) : (
                  filteredDoctors.map((ann) => (
                    <div
                      key={ann._id}
                      ref={(el) => { annotationItemRefs.current[ann._id] = el; }}
                      className={`wsi-annotation-item ${selectedId === ann._id || hoveredId === ann._id ? 'selected' : ''}`}
                      onClick={() => setSelectedId(ann._id || null)}
                      onMouseEnter={() => setHoveredId(ann._id || null)}
                      onMouseLeave={() => setHoveredId(null)}
                    >
                      <div className="wsi-annotation-color" style={{ background: ann.color || '#FF4444' }} />
                      <div className="wsi-annotation-info">
                        <div className="wsi-annotation-label">{ann.label || 'Unlabeled'}</div>
                        {ann.sublabel && <div className="wsi-annotation-sublabel">{ann.sublabel}</div>}
                        <div className="wsi-annotation-meta">
                          <span className="wsi-source-badge manual">Doctor</span>
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </>
            ) : filteredAnnotations.length === 0 ? (
              <p className="wsi-sidebar-empty">
                {readOnlyState
                  ? 'No annotations on this patch.'
                  : 'No annotations yet. Use Rect or Poly tools to draw.'}
              </p>
            ) : (
              filteredAnnotations.map((ann) => (
                <div
                  key={ann._id}
                  ref={(el) => { annotationItemRefs.current[ann._id] = el; }}
                  className={`wsi-annotation-item ${selectedId === ann._id || hoveredId === ann._id ? 'selected' : ''}`}
                  onClick={() => setSelectedId(ann._id || null)}
                  onMouseEnter={() => setHoveredId(ann._id || null)}
                  onMouseLeave={() => setHoveredId(null)}
                >
                  <div className="wsi-annotation-color" style={{ background: ann.color || '#FF4444' }} />
                  <div className="wsi-annotation-info">
                    <div className="wsi-annotation-label">{ann.label || 'Unlabeled'}</div>
                    {ann.sublabel && <div className="wsi-annotation-sublabel">{ann.sublabel}</div>}
                    {isAdminRoute && compareView !== 'both' && (
                      <div className="wsi-annotation-meta">
                        <span className={`wsi-source-badge ${compareView === 'original' ? 'ai' : 'manual'}`}>
                          {compareView === 'original' ? 'Original' : 'Doctor'}
                        </span>
                      </div>
                    )}
                  </div>
                  {!readOnlyState && (
                    <div className="wsi-annotation-actions">
                      <button
                        type="button"
                        className="wsi-ann-action-btn"
                        title="Edit label"
                        onClick={(e) => { e.stopPropagation(); handleEditLabel(ann); }}
                      >
                        ✏️
                      </button>
                      <button
                        type="button"
                        className="wsi-ann-action-btn delete"
                        title="Delete"
                        onClick={(e) => { e.stopPropagation(); handleDeleteAnnotation(ann._id!); }}
                      >
                        🗑️
                      </button>
                    </div>
                  )}
                </div>
              ))
            )}
          </div>
        </div>
      </div>

      {showLabelDialog && (
        <div
          className="wsi-label-overlay"
          onClick={() => { setShowLabelDialog(false); setPendingAnnotation(null); }}
        >
          <div className="wsi-label-dialog" onClick={(e) => e.stopPropagation()}>
            <h3>{editingAnnotationId ? 'Edit Annotation Label' : 'Assign Label'}</h3>

            <div className="wsi-label-field">
              <label>Cell Classification (Label)</label>
              <Select
                value={(() => {
                  const raw = (findLabelOption(labelValue)?.value ?? labelValue?.trim()) || 'NILM';
                  const allowed = new Set(labelSelectEntries.map((e) => e.value));
                  return allowed.has(raw) ? raw : 'NILM';
                })()}
                onValueChange={(v) => {
                  setLabelValue(v);
                  const opt = findLabelOption(v);
                  if (opt) {
                    setSublabelValue('None');
                    if (opt.color) setSelectedColor(opt.color);
                  }
                }}
              >
                <SelectTrigger className="w-full h-10 text-sm bg-slate-900 border border-slate-500 text-slate-100">
                  <SelectValue placeholder="Select classification" />
                </SelectTrigger>
                <SelectContent className="z-[1100] bg-slate-900 border-slate-600 text-slate-100">
                  {labelSelectEntries.map((opt) => (
                    <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="wsi-label-field">
              <label>Sub-Classification (Sublabel)</label>
              <Select
                value={sublabelChoices.includes(sublabelValue.trim()) ? sublabelValue.trim() : (sublabelChoices[0] || '')}
                onValueChange={setSublabelValue}
              >
                <SelectTrigger className="w-full h-10 text-sm bg-slate-900 border border-slate-500 text-slate-100">
                  <SelectValue placeholder="Select sub-classification" />
                </SelectTrigger>
                <SelectContent className="z-[1100] bg-slate-900 border-slate-600 text-slate-100 max-h-[min(60vh,320px)] overflow-y-auto">
                  {sublabelChoices.map((sub) => (
                    <SelectItem key={sub} value={sub}>{sub}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="wsi-label-field">
              <label>Annotation Color</label>
              <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                {COLORS.map((c) => (
                  <button
                    key={c}
                    type="button"
                    onClick={() => setSelectedColor(c)}
                    style={{
                      width: 24, height: 24, borderRadius: 4, background: c,
                      cursor: 'pointer',
                      border: selectedColor === c ? '2px solid white' : '2px solid transparent',
                      boxShadow: selectedColor === c ? '0 0 0 2px #3b82f6' : 'none',
                    }}
                    title={LABEL_OPTIONS.find((l) => l.color === c)?.label || c}
                  />
                ))}
              </div>
            </div>

            <div className="wsi-label-actions">
              <button type="button" className="cancel-btn" onClick={() => { setShowLabelDialog(false); setPendingAnnotation(null); }}>
                Cancel
              </button>
              <button type="button" className="save-btn" onClick={handleSaveLabel}>
                {editingAnnotationId ? 'Update' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}

      {error && (
        <div className="fixed bottom-4 right-4 bg-red-900/90 text-red-100 px-4 py-2 rounded text-sm">
          {error}
        </div>
      )}
    </div>
  );
}
